import { useNavigate } from "react-router-dom";

interface LoginPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: "bookmark" | "invest" | "save" | "optimize";
}

/**
 * Modal that prompts unauthenticated users to log in
 * when they try to perform an action that requires authentication.
 */
export function LoginPromptModal({
  isOpen,
  onClose,
  action,
}: LoginPromptModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const actionText = {
    bookmark: "bookmark strategies",
    invest: "invest in strategies",
    save: "save your strategies",
    optimize: "optimize strategies with AI",
  };

  const handleLogin = () => {
    onClose();
    navigate("/login");
  };

  const handleSignUp = () => {
    onClose();
    navigate("/signup");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close modal"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md bg-white dark:bg-surface-900 border border-gray-200 dark:border-surface-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 dark:border-surface-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-brand-500/20 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-purple-600 dark:text-brand-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-surface-100">
                Sign in required
              </h2>
              <p className="text-sm text-gray-600 dark:text-surface-400">
                Create a free account to continue
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 bg-white dark:bg-surface-900">
          <p className="text-gray-700 dark:text-surface-300 text-sm leading-relaxed">
            You need to be signed in to {actionText[action]}. Create a free
            account to unlock all features including:
          </p>
          <ul className="mt-4 space-y-2">
            <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-surface-300">
              <svg
                className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Bookmark your favorite strategies
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-surface-300">
              <svg
                className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Invest in strategies with real capital
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-surface-300">
              <svg
                className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Save and manage your custom strategies
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700 dark:text-surface-300">
              <svg
                className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Track your portfolio performance
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-surface-800/50 border-t border-gray-200 dark:border-surface-700/50 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-surface-600 text-gray-700 dark:text-surface-300 hover:bg-gray-100 dark:hover:bg-surface-700/50 transition-colors"
          >
            Maybe later
          </button>
          <button
            type="button"
            onClick={handleLogin}
            className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-surface-600 text-gray-900 dark:text-surface-100 hover:bg-gray-100 dark:hover:bg-surface-700/50 transition-colors"
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={handleSignUp}
            className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors"
          >
            Sign Up Free
          </button>
        </div>
      </div>
    </div>
  );
}
